<template>
  <v-card>
    <v-card-title>
      <v-icon color="primary" class="me-2">mdi-rocket-launch-outline</v-icon>
      {{ $t('getting_started') }}
      <v-spacer></v-spacer>
      <span class="text-body-2">{{ $t('n_of_n_done', { done: done, total: total }) }}</span>
    </v-card-title>
    <v-card-subtitle>{{ $t('getting_started_hint') }}</v-card-subtitle>

    <v-progress-linear
      :value="total ? (done / total) * 100 : 0"
      height="6"
      color="success"
    ></v-progress-linear>

    <v-card-text>
      <vue-element-loading :active="isLoading" />

      <v-alert v-if="complete" type="success" text class="mb-4">
        {{ $t('setup_complete') }}
      </v-alert>

      <v-list two-line>
        <v-list-item v-for="(step, index) in steps" :key="step.key">
          <v-list-item-avatar>
            <v-icon :color="step.done ? 'success' : (isNext(index) ? 'primary' : 'grey')">
              {{ step.done ? 'mdi-check-circle' : 'mdi-circle-outline' }}
            </v-icon>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title :class="{ 'text--disabled': step.done }">
              {{ index + 1 }}. {{ $t('setup_step_' + step.key) }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ $t('setup_step_' + step.key + '_hint') }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action class="flex-row align-center">
            <v-chip v-if="step.count" x-small outlined class="me-3">
              {{ step.count }}
            </v-chip>
            <v-btn
              small
              :text="step.done"
              :color="step.done ? 'secondary' : 'primary'"
              :to="{ name: step.route }"
            >
              {{ step.done ? $t('open') : $t('do_this') }}
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>

      <!-- the fastest path through the first four steps for a school that
           already keeps its lists in a spreadsheet, which is all of them -->
      <v-divider class="my-3"></v-divider>
      <div class="d-flex align-center flex-wrap">
        <v-icon color="primary" class="me-2">mdi-file-upload-outline</v-icon>
        <span class="me-4">{{ $t('already_have_a_spreadsheet') }}</span>
        <v-btn small color="primary" :to="{ name: 'imports' }">
          {{ $t('import_data') }}
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
/*
 * The order the panel has to be set up in, which nothing said.
 *
 * A school signing up met eight screens in a menu, each reading "No data
 * available", and worked the order out by failing: a route drawn before there
 * are stops is an empty map, a trip with no bus cannot run, and children
 * mapped before there are trips have nothing to be mapped to.
 *
 * Each step counts what the school already has, so this is its own progress
 * rather than a tutorial - and once it is finished the link stops appearing in
 * the menu instead of sitting there forever as a reminder of nothing.
 */
import VueElementLoading from "vue-element-loading";

export default {
  name: "SetupIndex",
  components: { VueElementLoading },
  data() {
    return {
      steps: [],
      done: 0,
      total: 0,
      complete: false,
      isLoading: false,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    //the first step still to do, which is the only one worth pointing at
    isNext(index) {
      for (let i = 0; i < this.steps.length; i++) {
        if (!this.steps[i].done) return i === index;
      }
      return false;
    },
    load() {
      this.isLoading = true;
      axios
        .get("/school-readiness/setup")
        .then((response) => {
          this.steps = response.data.steps || [];
          this.done = response.data.done || 0;
          this.total = response.data.total || 0;
          this.complete = !!response.data.complete;
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_loading_setup"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
